import { defineComponent as _defineComponent } from 'vue'
import moment from 'moment'
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex-composition-helpers'
import { BatchPaymentTerms, PAYMENT_TERMS_OPTIONS } from '@/models/invoice'
import { useCreateInvoiceMutation } from '@/api/invoice'
import { AxiosError } from 'axios'
import { getDueDate } from '@/utils/invoice-due-date'
import VsAutocomplete from '@/components/vision/Inputs/VsAutocomplete/VsAutocomplete.vue'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import VsHeading from '@/components/vision/VsHeading.vue'
import VsForm from '@/components/vision/VsForm.vue'
import VsTextInput from '@/components/vision/Inputs/VsTextInput.vue'
import VsDatePicker from '@/components/vision/Inputs/VsDatePicker.vue'
import VsButton from '@/components/vision/VsButton.vue'

interface FormData {
  batchNumber?: string | null
  batchDate?: moment.Moment | null
  paymentTerms?: BatchPaymentTerms | null
  dueDate?: moment.Moment | null
  memo?: string | null
}


export default /*#__PURE__*/_defineComponent({
  __name: 'BatchCreationForm',
  props: {
    initialBatchNumberValue: null,
    buttonLabel: null,
    addDivider: { type: Boolean }
  },
  emits: ["batch-created-success", "batch-created-failure", "discard"],
  setup(__props: any, { emit }: { emit: ({
  (
    e: 'batch-created-success',
    data: {
      batchId: string
      batchNumber: string | null
    }
  )
  (e: 'batch-created-failure', error: unknown)
  (e: 'discard')
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  initialBatchNumberValue: string
  buttonLabel?: string
  addDivider?: boolean
}



const formData = reactive<FormData>({
  batchNumber: undefined,
  batchDate: undefined,
  paymentTerms: undefined,
  dueDate: undefined,
  memo: undefined,
})

watch(
  () => props.initialBatchNumberValue,
  (newVal) => {
    formData.batchNumber = newVal
  },
  { immediate: true }
)

onMounted(() => {
  formData.batchDate = moment()
})

const {
  mutateAsync: createBatch,
  isLoading: creatingBatch,
  error: createBatchError,
} = useCreateInvoiceMutation()

const store = useStore()
const organizationId = computed<string>(
  () => store.getters['permissions/getOrganizationId']
)

const batchForm = ref()


function setBatchNumber(batchNumber: string) {
  formData.batchNumber = batchNumber
}

function setBatchDate(batchDate: moment.Moment) {
  formData.batchDate = batchDate
  if (formData.paymentTerms && formData.batchDate) {
    formData.dueDate = getDueDate(formData.batchDate, formData.paymentTerms)
  }
}

function setDueDate(dueDate: moment.Moment) {
  formData.dueDate = dueDate
}

function setPaymentTerms(newPaymentTerms: BatchPaymentTerms | null) {
  formData.paymentTerms = newPaymentTerms
  if (formData.paymentTerms && formData.batchDate) {
    formData.dueDate = getDueDate(formData.batchDate, formData.paymentTerms)
  }
}

function setMemo(newMemo: string | null) {
  formData.memo = newMemo
}

async function save() {
  if (batchForm.value.isValid()) {
    try {
      const response = await createBatch({
        id: null, // Let the backend auto-populate
        invoiceNumber: formData.batchNumber ?? null,
        invoiceDate: formData.batchDate?.utc() ?? moment().utc(),
        paymentTerms: formData.paymentTerms ?? BatchPaymentTerms.DueOnReceipt,
        dueDate: formData.dueDate ?? null,
        jobIds: null,
        ownerId: organizationId.value,
        memo: formData.memo ?? null,
      })

      emit('batch-created-success', {
        batchId: response.data.id as string,
        batchNumber: formData.batchNumber as string,
      })
    } catch (e) {
      if (
        createBatchError.value &&
        (createBatchError.value as AxiosError).response?.status === 409
      ) {
        emit(
          'batch-created-failure',
          `Batch number "${formData.batchNumber}" already exists`
        )
        return
      }
      emit('batch-created-failure', createBatchError.value)
    }
  }
}

function discard() {
  emit('discard')
}

return { __sfc: true,props, formData, createBatch, creatingBatch, createBatchError, store, organizationId, batchForm, emit, setBatchNumber, setBatchDate, setDueDate, setPaymentTerms, setMemo, save, discard, BatchPaymentTerms, PAYMENT_TERMS_OPTIONS, VsAutocomplete, VsWrapper, VsHeading, VsForm, VsTextInput, VsDatePicker, VsButton }
}

})